import React, { useEffect, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { PageHeader, PrimaryButton, SecondaryButton, Section, EmptyState, TextField, TextArea } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { MailCheck, Send, X, Clock, CheckCircle2, XCircle, Sparkles, HandHeart, Settings } from "lucide-react";
import { toast, Toaster } from "sonner";

const TABS = [
  { key: "a_valider", label: "À valider", icon: Clock },
  { key: "envoye", label: "Envoyés", icon: CheckCircle2 },
  { key: "rejete", label: "Rejetés", icon: XCircle },
];

const TYPE_META = {
  presentation: { label: "Présentation", icon: Sparkles, color: "text-[#B8873A] bg-amber-50 border-amber-200" },
  remerciement: { label: "Remerciement", icon: HandHeart, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
};

export default function EmailValidation() {
  const { user } = useAuth();
  const [tab, setTab] = useState("a_valider");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ presentation_auto: false, remerciement_auto: false });
  const canSettings = ["admin", "dirigeant", "secretaire_bo"].includes(user?.role);

  const load = (t = tab) => {
    setLoading(true);
    api.get(`/email-queue?statut=${t}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { api.get("/settings/email-automation").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const toggle = async (key) => {
    const next = { ...settings, [key]: !settings[key] };
    setSettings(next);
    try {
      await api.put("/settings/email-automation", { [key]: next[key] });
      toast.success(next[key] ? "Envoi automatique activé" : "Validation manuelle activée");
    } catch { toast.error("Erreur"); setSettings(settings); }
  };

  const counts = () => load(tab);

  return (
    <div className="p-8" data-testid="email-validation-page">
      <Toaster position="top-right" richColors />
      <PageHeader title="Validation des emails" subtitle="Emails clients à valider avant envoi · Présentation & Remerciement" />

      {canSettings && (
        <Section title="Réglages d'envoi">
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <ToggleRow
              label="Email de présentation (à l'import d'un prospect)"
              icon={Sparkles}
              value={settings.presentation_auto}
              onToggle={() => toggle("presentation_auto")}
              testid="toggle-presentation"
            />
            <ToggleRow
              label="Email de remerciement (à la signature)"
              icon={HandHeart}
              value={settings.remerciement_auto}
              onToggle={() => toggle("remerciement_auto")}
              testid="toggle-remerciement"
            />
          </div>
          <div className="px-5 pb-4 text-xs text-slate-500 flex items-center gap-2">
            <Settings className="w-3.5 h-3.5" /> « Automatique » = l'email part immédiatement. « Validation manuelle » = il apparaît dans « À valider » ci-dessous.
          </div>
        </Section>
      )}

      <div className="flex gap-1 border-b border-slate-200 mb-5">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t.key ? "border-[#B8873A] text-[#111111]" : "border-transparent text-slate-500 hover:text-slate-800"}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-10 text-center">Chargement…</div>
      ) : items.length === 0 ? (
        <EmptyState icon={MailCheck} title={tab === "a_valider" ? "Aucun email en attente" : "Rien à afficher"} description={tab === "a_valider" ? "Les emails de présentation et de remerciement apparaîtront ici pour validation." : ""} />
      ) : (
        <div className="space-y-4">
          {items.map((it) => (
            <EmailCard key={it.id} item={it} editable={tab === "a_valider"} onChanged={counts} />
          ))}
        </div>
      )}
    </div>
  );
}

function ToggleRow({ label, icon: Icon, value, onToggle, testid }) {
  return (
    <div className="flex items-center justify-between border border-slate-200 rounded-sm p-3">
      <div className="flex items-center gap-2.5">
        <Icon className="w-4 h-4 text-slate-500" />
        <span className="text-sm font-medium text-[#111111]">{label}</span>
      </div>
      <button
        onClick={onToggle}
        data-testid={testid}
        className={`relative w-14 h-7 rounded-full transition-colors ${value ? "bg-[#B8873A]" : "bg-slate-300"}`}
      >
        <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full transition-transform ${value ? "translate-x-7" : ""}`} />
        <span className="sr-only">{value ? "Automatique" : "Manuel"}</span>
      </button>
    </div>
  );
}

function EmailCard({ item, editable, onChanged }) {
  const meta = TYPE_META[item.type] || TYPE_META.presentation;
  const [sujet, setSujet] = useState(item.sujet);
  const [corps, setCorps] = useState(item.corps);
  const [busy, setBusy] = useState(false);
  const dirty = sujet !== item.sujet || corps !== item.corps;

  const saveIfDirty = async () => {
    if (dirty) await api.put(`/email-queue/${item.id}`, { sujet, corps });
  };

  const send = async () => {
    setBusy(true);
    try {
      await saveIfDirty();
      await api.post(`/email-queue/${item.id}/send`);
      toast.success(`Email envoyé à ${item.recipient_name}`);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!window.confirm("Rejeter cet email (il ne sera pas envoyé) ?")) return;
    setBusy(true);
    try {
      await api.post(`/email-queue/${item.id}/reject`);
      toast.success("Email rejeté");
      onChanged();
    } catch { toast.error("Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid={`email-card-${item.id}`}>
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${meta.color}`}>
            <meta.icon className="w-3.5 h-3.5" /> {meta.label}
          </span>
          <div>
            <div className="text-sm font-semibold text-[#111111]">{item.recipient_name}</div>
            <div className="text-xs text-slate-500">{item.recipient_email}</div>
          </div>
        </div>
        <div className="text-xs text-slate-400">{formatDate(item.created_at)}</div>
      </div>

      {editable ? (
        <div className="space-y-3">
          <TextField label="Objet" value={sujet} onChange={(e) => setSujet(e.target.value)} testid={`edit-sujet-${item.id}`} />
          <TextArea label="Message" rows={8} value={corps} onChange={(e) => setCorps(e.target.value)} testid={`edit-corps-${item.id}`} />
          <div className="flex items-center justify-end gap-2">
            <SecondaryButton onClick={reject} disabled={busy} className="!text-red-700 !border-red-300 hover:!bg-red-50" data-testid={`reject-${item.id}`}>
              <span className="flex items-center gap-1.5"><X className="w-4 h-4" /> Rejeter</span>
            </SecondaryButton>
            <PrimaryButton onClick={send} disabled={busy} data-testid={`send-${item.id}`}>
              <span className="flex items-center gap-1.5"><Send className="w-4 h-4" /> {busy ? "Envoi…" : (dirty ? "Enregistrer & envoyer" : "Envoyer")}</span>
            </PrimaryButton>
          </div>
        </div>
      ) : (
        <div>
          <div className="text-sm font-semibold text-[#111111] mb-1">{item.sujet}</div>
          <div className="text-sm text-slate-600 whitespace-pre-line line-clamp-4">{item.corps}</div>
          {item.error && <div className="text-xs text-red-600 mt-2">Erreur : {item.error}</div>}
        </div>
      )}
    </div>
  );
}
